import { AdvantagesSection, GuidesSection, JourneySection, StoriesSection } from './paywall';

export interface VoicesFreeUsageStatus {
  freeSessionsLimit: number;
  lastSessionUsage: Date | null;
  usedSessions: number;
  reachLimit: boolean;
}

export enum PaymentProviders {
  Stripe = 'stripe',
  IAPHub = 'iab_hub',
  Circles = 'circles',
  Paypal = 'paypal',
}

export interface PurchaseMeta {
  enableProgram?: boolean;
}

export interface GetPaymentStatusResult {
  hasActiveVoicesPremiumSubscription: boolean;
  hasVoicesLimitUsageSubscription: boolean;
  hasPurchasedVoicesPremiumSubscription: boolean;
  voicesFreeUsageStatus: VoicesFreeUsageStatus;
  shouldDisplayFreePremiumDrawer: boolean;
  isActivePayer: boolean;
  withinFreeTrial: boolean;
  isGrantedPayment: boolean;
  paymentProvider: PaymentProviders | null;
  purchaseMeta: PurchaseMeta;
}

// Components for building purchase screen on mobile
export type Location = 'scroll' | 'sticky';

export interface BaseComponent {
  displayOnlyOn?: 'ios' | 'android';

  location: Location;
}

export interface HeaderProps {
  preset: string; // must be the same as existing mobile TextPreset
  blackText: string;
  gradientText: string;
}

export interface Header extends BaseComponent {
  type: 'header';
  props: HeaderProps;
}

export interface PlainText extends BaseComponent {
  type: 'text';
  props: {
    preset: string; // must be the same as existing mobile TextPreset
    text: string;
    color?: string;
  };
}

export interface Benefit {
  description: string;
  plus: boolean | string;
  textColor?: string;
  iconColor?: string;
  iconWrapperColor?: string;
}

export interface BenefitsProps {
  headerDescription: string;
  headerPlus: string;
  benefits: Benefit[];
}

export interface Benefits extends BaseComponent {
  type: 'benefits';
  props: BenefitsProps;
}

export interface Products extends BaseComponent {
  type: 'products';
  location: Location;
}

export interface RatingImage extends BaseComponent {
  type: 'rating';
}

export interface QuoteProps {
  rating: number;
  text: string;
  author: string;
}

export interface Quote extends BaseComponent {
  type: 'quote';
  props: QuoteProps;
}

export interface Image extends BaseComponent {
  type: 'image';
  props: {
    url: string;
  };
}

export interface Button extends BaseComponent {
  type: 'button';
  props: {
    text: string;
    textAfterTrial: string;
    action: string;
    type: string; // must be the same as existing mobile BUTTON_TYPES enum
  };
}

export interface PromoCode extends BaseComponent {
  type: 'promo_code';
  props: {
    text: string;
  };
}

// paywall V2
export interface Advantages extends BaseComponent {
  type: 'advantages';
  props: AdvantagesSection;
}
export interface Guides extends BaseComponent {
  type: 'guides';
  props: GuidesSection;
}
export interface Journey extends BaseComponent {
  type: 'journey';
  props: JourneySection;
}
export interface ProductsV2 extends BaseComponent {
  type: 'productsV2';
  props: {
    title: string;
    bestValueLabel: string;
    promoCode?: { displayOnlyOn: BaseComponent['displayOnlyOn']; text: string };
  };
}
export interface Stories extends BaseComponent {
  type: 'stories';
  props: StoriesSection;
}

export type Component =
  | Header
  | PlainText
  | Benefits
  | Products
  | RatingImage
  | Quote
  | Image
  | Button
  | PromoCode
  | Advantages
  | Guides
  | Journey
  | ProductsV2
  | Stories;
export type Components = Component[];

export interface LatestPurchaseDetails {
  purchaseId?: string;
  productId?: string;
  productSku?: string;
  provider?: string;
  dateCreated?: Date;
  productType?: string;
  price?: number;
  expirationDate?: Date;
  renewCount?: number;
  cancellationDate?: Date;
  subscriptionStatus?: string;
  lastUpdated?: string;
  paymentCustomerId?: string;
  paymentMethodId?: string;
  cancellationStatus?: string;
  cancellationRequestTimestamp?: Date;
}
